/**
 * Unified Agent Registry — single source of truth used by:
 *   • AgentsPage (4 suite grids)
 *   • RequisitionsPage L2 kit
 *   • Global ⌘K AgentLauncher
 *
 * When you add a new sub-module + agent persona to any of the source arrays
 * (SS_MODULES, SCM_MODULES, SRM_MODULES, RQ_MODULES), the launcher and grids
 * pick it up automatically — no other file needs editing here.
 *
 * Each entry: { module, name, code, label, suite, tone, route }
 *   • module  — agent module key sent to /api/v1/sourcing/agents/{module}/chat
 *   • name    — human-readable agent persona
 *   • code    — sub-module code (e.g. SS-4.1, SCM-1, RQ-5.6)
 *   • label   — short sub-module name (e.g. "Strategy & Planning")
 *   • suite   — one of: 'Sourcing' | 'Category & Spend' | 'Supplier Management' | 'Requisitions'
 *   • tone    — Tailwind colour stem (e.g. 'sky', 'violet')
 *   • route   — deep-link to the sub-module page (used for navigation)
 */
import {
  Boxes, BarChart2, Compass, FileText, Sparkles, Send, Users,
  Award as AwardIcon, ShieldAlert, Database, Shield, Heart, Handshake,
  Bot,
} from 'lucide-react';
import { SS_MODULES } from '@/components/sourcing/SourcingL2Kit';
import { AGENT_MAP as SS_AGENT_MAP } from '@/components/sourcing/HowItWorksContent';
import { RQ_MODULES, RQ_AGENT_MAP } from '@/components/requisitions/RequisitionsL2Kit';

/* ─────────── Category & Spend (SCM-1 → SCM-6) ─────────── */
export const SCM_MODULES = [
  { code: 'SCM-1', key: 'scm1', label: 'Taxonomy',        Icon: Boxes,        tone: 'sky',     route: '/app/scm/taxonomy',     name: 'Taxonomy Architect Agent' },
  { code: 'SCM-2', key: 'scm2', label: 'Spend Analytics', Icon: BarChart2,    tone: 'indigo',  route: '/app/scm/spend-cube',   name: 'Spend Cube Storyteller Agent' },
  { code: 'SCM-3', key: 'scm3', label: 'Kraljic',         Icon: Compass,      tone: 'purple',  route: '/app/scm/kraljic',      name: 'Kraljic Strategist Agent' },
  { code: 'SCM-4', key: 'scm4', label: 'Strategy',        Icon: FileText,     tone: 'fuchsia', route: '/app/scm/strategy-doc', name: 'Category Strategy Drafter Agent' },
  { code: 'SCM-5', key: 'scm5', label: 'Market Intel',    Icon: Sparkles,     tone: 'amber',   route: '/app/scm/market-intel', name: 'Market Intelligence Scout Agent' },
  { code: 'SCM-6', key: 'scm6', label: 'Demand',          Icon: Send,         tone: 'emerald', route: '/app/scm/demand',       name: 'Demand Forecaster Agent' },
];

/* ─────────── Supplier Management (SRM-1 → SRM-8) ─────────── */
export const SRM_MODULES = [
  { code: 'SRM-1', key: 'srm1', label: 'Onboarding',      Icon: Users,        tone: 'sky',     route: '/app/srm/registration',     name: 'Onboarding Concierge Agent' },
  { code: 'SRM-2', key: 'srm2', label: 'Qualification',   Icon: AwardIcon,    tone: 'indigo',  route: '/app/srm/qualification',    name: 'Qualification Auditor Agent' },
  { code: 'SRM-3', key: 'srm3', label: 'Performance',     Icon: BarChart2,    tone: 'amber',   route: '/app/srm/performance',      name: 'Performance Coach Agent' },
  { code: 'SRM-4', key: 'srm4', label: 'Relationship',    Icon: Heart,        tone: 'rose',    route: '/app/srm/relationship',     name: 'Relationship Steward Agent' },
  { code: 'SRM-5', key: 'srm5', label: 'Risk',            Icon: ShieldAlert,  tone: 'fuchsia', route: '/app/srm/risk',             name: 'Risk Sentinel Agent' },
  { code: 'SRM-6', key: 'srm6', label: 'Data Governance', Icon: Database,     tone: 'slate',   route: '/app/srm/data-governance',  name: 'Data Quality Steward Agent' },
  { code: 'SRM-7', key: 'srm7', label: 'Diversity',       Icon: Shield,       tone: 'teal',    route: '/app/srm/diversity',        name: 'Diversity Advocate Agent' },
  { code: 'SRM-8', key: 'srm8', label: 'Collaboration',   Icon: Handshake,    tone: 'violet',  route: '/app/srm/collaboration',    name: 'Collaboration Concierge Agent' },
];

/* Umbrella agents that don't belong to a sub-module grid */
const UMBRELLA_AGENTS = [
  { module: 'rq5', name: 'Requisitions Concierge Agent', code: 'RQ-5',
    label: 'Requisitions hub', suite: 'Requisitions', tone: 'sky',
    route: '/app/requisitions', Icon: Bot },
];

/* ─────────── ALL_AGENTS — the unified, derived registry ─────────── */
export const ALL_AGENTS = [
  // Strategic Sourcing (16) — derive from SS_MODULES + SS_AGENT_MAP
  ...SS_MODULES.map(m => {
    const a = SS_AGENT_MAP[m.key] || {};
    return {
      module: a.module || m.key.toLowerCase(),
      name: a.name || `${m.label} Agent`,
      code: m.code,
      label: m.label,
      suite: 'Sourcing',
      tone: m.tone,
      route: m.route,
      Icon: m.Icon,
    };
  }),
  // Category & Spend (6)
  ...SCM_MODULES.map(m => ({
    module: m.key, name: m.name, code: m.code, label: m.label,
    suite: 'Category & Spend', tone: m.tone, route: m.route, Icon: m.Icon,
  })),
  // Supplier Management (8)
  ...SRM_MODULES.map(m => ({
    module: m.key, name: m.name, code: m.code, label: m.label,
    suite: 'Supplier Management', tone: m.tone, route: m.route, Icon: m.Icon,
  })),
  // Requisitions sub-modules (8) — derive from RQ_MODULES + RQ_AGENT_MAP
  ...RQ_MODULES.map(m => {
    const a = RQ_AGENT_MAP[m.key] || {};
    return {
      module: a.module || `rq5_${m.key}`,
      name: a.name || `${m.label} Agent`,
      code: m.code,
      label: m.label,
      suite: 'Requisitions',
      tone: m.tone,
      // Tabbed page — deep-link with ?tab=
      route: `/app/requisitions?tab=${m.key}`,
      Icon: m.Icon,
    };
  }),
  // Umbrella agents
  ...UMBRELLA_AGENTS,
];

/* Quick lookup by module key (e.g. lookupAgent('rq56')). */
export const AGENT_BY_MODULE = ALL_AGENTS.reduce((acc, a) => {
  acc[a.module] = a; return acc;
}, {});

export const SUITE_ORDER = ['Sourcing', 'Category & Spend', 'Supplier Management', 'Requisitions'];
